// src/ConveCreateGroupModal.tsx

import React, { useState, useCallback, useEffect } from 'react';
import { UserSearchResult } from './types/groupChat';
import { groupChatApi } from './api/groupChatApi';

interface Props {
  onClose: () => void;
  onCreated: (roomId: number) => void;
  currentUserId: number;
}

const ConveCreateGroupModal: React.FC<Props> = ({ onClose, onCreated, currentUserId }) => {
  const [chatType, setChatType] = useState<'1on1' | 'GROUP'>('1on1');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<UserSearchResult[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<UserSearchResult[]>([]);
  const [groupTitle, setGroupTitle] = useState('');
  const [aiEnabled, setAiEnabled] = useState(true);
  const [creating, setCreating] = useState(false);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (searchQuery.trim().length < 1) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        setSearching(true);
        const results = await groupChatApi.searchUsers(searchQuery.trim());
        setSearchResults(results);
      } catch {
        // alert는 api에서 처리
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const toggleUser = useCallback((user: UserSearchResult) => {
    setSelectedUsers((prev) => {
      const exists = prev.some((u) => u.id === user.id);
      if (chatType === '1on1') {
        return exists ? [] : [user];
      }
      return exists ? prev.filter((u) => u.id !== user.id) : [...prev, user];
    });
  }, [chatType]);

  const handleCreate = async () => {
    if (selectedUsers.length === 0) {
      alert('대화 상대를 선택해주세요.');
      return;
    }
    try {
      setCreating(true);
      let room;
      if (chatType === '1on1') {
        room = await groupChatApi.createDirectRoom(selectedUsers[0].id);
      } else {
        room = await groupChatApi.createGroupRoom({
          title: groupTitle.trim() || selectedUsers.map((u) => u.name).join(', '),
          memberIds: selectedUsers.map((u) => u.id),
          aiEnabled,
        });
      }
      onCreated(room.id);
    } catch {
      // alert는 api에서 처리
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="conve-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="conve-modal">
        <div className="conve-modal-header">
          <h3>새 대화</h3>
          <button className="conve-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="conve-modal-body">
          {/* 채팅 유형 선택 */}
          <div className="conve-chat-type-selector">
            <button
              className={`conve-type-btn ${chatType === '1on1' ? 'active' : ''}`}
              onClick={() => { setChatType('1on1'); setSelectedUsers([]); }}
            >
              💬 1:1 채팅
            </button>
            <button
              className={`conve-type-btn ${chatType === 'GROUP' ? 'active' : ''}`}
              onClick={() => { setChatType('GROUP'); setSelectedUsers([]); }}
            >
              👥 그룹 채팅
            </button>
          </div>

          {/* 그룹 채팅 설정 */}
          {chatType === 'GROUP' && (
            <div className="conve-group-settings">
              <input
                className="conve-modal-input"
                type="text"
                placeholder="방 이름 (선택)"
                value={groupTitle}
                onChange={(e) => setGroupTitle(e.target.value)}
                maxLength={50}
              />
              <div className="conve-ai-toggle-row">
                <span>AI 참여 허용</span>
                <label className="conve-toggle">
                  <input
                    type="checkbox"
                    checked={aiEnabled}
                    onChange={(e) => setAiEnabled(e.target.checked)}
                  />
                  <span className="conve-toggle-slider" />
                </label>
              </div>
            </div>
          )}

          {/* 선택된 사용자 */}
          {selectedUsers.length > 0 && (
            <div className="conve-selected-users">
              {selectedUsers.map((u) => (
                <span key={u.id} className="conve-selected-chip">
                  {u.name}
                  <button onClick={() => toggleUser(u)}>✕</button>
                </span>
              ))}
            </div>
          )}

          {/* 사용자 검색 */}
          <input
            className="conve-modal-input"
            type="text"
            placeholder="이름 또는 이메일 검색..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />

          <div className="conve-search-results">
            {searching && <div className="conve-searching">검색 중...</div>}
            {!searching && searchResults.length === 0 && searchQuery.trim().length > 0 && (
              <div className="conve-no-results">검색 결과가 없습니다</div>
            )}
            {searchResults.map((user) => {
              const isSelected = selectedUsers.some((u) => u.id === user.id);
              return (
                <div
                  key={user.id}
                  className={`conve-search-user-item ${isSelected ? 'selected' : ''}`}
                  onClick={() => toggleUser(user)}
                >
                  <div className="conve-search-user-avatar">
                    {user.name[0]?.toUpperCase()}
                  </div>
                  <div className="conve-search-user-info">
                    <div className="conve-search-user-name">{user.name}</div>
                    <div className="conve-search-user-email">{user.email}</div>
                  </div>
                  {isSelected && <span className="conve-check">✓</span>}
                </div>
              );
            })}
          </div>
        </div>

        <div className="conve-modal-footer">
          <button className="conve-modal-cancel" onClick={onClose}>취소</button>
          <button
            className="conve-modal-confirm"
            onClick={handleCreate}
            disabled={creating || selectedUsers.length === 0}
          >
            {creating ? '생성 중...' : '대화 시작'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConveCreateGroupModal;
